import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchEnvironments as fetchEnvironmentsAPI } from '../utils/environmentService';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useCallback } from 'react';

// Cache duration in milliseconds (1 hour)
const CACHE_DURATION = parseInt(process.env.REACT_APP_CACHE_DURATION, 10) || 60 * 60 * 1000;

// Async thunk for fetching environments
export const fetchEnvironments = createAsyncThunk(
  'environment/fetchEnvironments',
  async (_, { rejectWithValue }) => {
    try {
      const environments = await fetchEnvironmentsAPI();
      return environments;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  selectedEndpoint: '',
  selectedEnvironment: '',
  environments: {},
  loading: false,
  error: null,
  lastFetched: null,
};

const environmentSlice = createSlice({
  name: 'environment',
  initialState,
  reducers: {
    setEnvironment(state, action) {
      state.selectedEndpoint = action.payload.endpoint;
      state.selectedEnvironment = action.payload.environmentName;
    },
    clearEnvironment(state) {
      state.selectedEndpoint = '';
      state.selectedEnvironment = '';
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEnvironments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEnvironments.fulfilled, (state, action) => {
        state.loading = false;
        state.environments = action.payload;
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(fetchEnvironments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Selectors
export const selectEnvironments = (state) => state.environment.environments;
export const selectEnvironmentsLoading = (state) => state.environment.loading;
export const selectEnvironmentsError = (state) => state.environment.error;
export const selectLastFetched = (state) => state.environment.lastFetched;
export const selectSelectedEnvironment = (state) => state.environment.selectedEnvironment;
export const selectSelectedEndpoint = (state) => state.environment.selectedEndpoint;

// Check if cache is still valid
export const selectIsCacheValid = (state) => {
  const lastFetched = state.environment.lastFetched;
  if (!lastFetched) return false;
  return (Date.now() - lastFetched) < CACHE_DURATION;
};

// Custom hook for using environments with automatic caching
export const useEnvironments = () => {
  const dispatch = useDispatch();
  const environments = useSelector(selectEnvironments);
  const loading = useSelector(selectEnvironmentsLoading);
  const error = useSelector(selectEnvironmentsError);
  const isCacheValid = useSelector(selectIsCacheValid);

  const fetchEnvironmentsIfNeeded = useCallback(() => {
    if (!isCacheValid && !loading) {
      dispatch(fetchEnvironments());
    }
  }, [dispatch, isCacheValid, loading]);

  useEffect(() => {
    fetchEnvironmentsIfNeeded();
  }, [fetchEnvironmentsIfNeeded]);

  const refetch = useCallback(() => {
    dispatch(fetchEnvironments());
  }, [dispatch]);

  return {
    environments,
    loading,
    error,
    refetch,
    hasData: Object.keys(environments).length > 0,
  };
};

export const { setEnvironment, clearEnvironment, clearError } = environmentSlice.actions;
export default environmentSlice.reducer; 